import React, { useState, useRef, useEffect } from 'react';
import {
    Container, Form, Button, Alert, Row, Col, Spinner,
    Card, ProgressBar, Table, Badge,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFileExcel, faDownload, faCloudUploadAlt, faSearch,
} from '@fortawesome/free-solid-svg-icons';
import api from '../api';

const POLL_MS = 1500;

const ListinoImport = () => {
    const [file, setFile]           = useState(null);
    const [throttle, setThrottle]   = useState(1.5);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress]   = useState(null);
    const [ultimoEan, setUltimoEan] = useState('');
    const [result, setResult]       = useState(null);
    const [error, setError]         = useState('');
    const pollingRef = useRef(false);

    useEffect(() => () => { pollingRef.current = false; }, []);

    const handleFileChange = (e) => {
        setFile(e.target.files[0] || null);
        setResult(null);
        setError('');
        setProgress(null);
    };

    const pollJob = async (jobId) => {
        pollingRef.current = true;
        while (pollingRef.current) {
            await new Promise(r => setTimeout(r, POLL_MS));
            if (!pollingRef.current) return;
            try {
                const { data } = await api.get(`/admin/listino/import-progress/${jobId}`);
                setProgress(data.stats);
                setUltimoEan(data.ultimo_ean || '');
                if (data.state === 'completed') {
                    setResult({ jobId, stats: data.stats, filename: data.result_filename });
                    pollingRef.current = false;
                    setUploading(false);
                    return;
                }
                if (data.state === 'error') {
                    setError(data.error || 'Errore durante l\'elaborazione');
                    pollingRef.current = false;
                    setUploading(false);
                    return;
                }
            } catch (err) {
                setError(`Errore polling: ${err.response?.data?.detail || err.message}`);
                pollingRef.current = false;
                setUploading(false);
                return;
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!file) return;
        setUploading(true);
        setError('');
        setResult(null);
        setProgress({ totale: 0, processati: 0, trovati: 0, non_trovati: 0, errori: 0 });
        setUltimoEan('');

        const fd = new FormData();
        fd.append('file', file);

        try {
            const { data } = await api.post(
                `/admin/listino/import-catalog?throttle=${throttle}`,
                fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            if (!data.job_id) throw new Error('Risposta non valida (job_id mancante)');
            pollJob(data.job_id);
        } catch (err) {
            setError(`Errore: ${err.response?.data?.detail || err.message}`);
            setUploading(false);
        }
    };

    const handleDownload = async () => {
        if (!result?.jobId) return;
        try {
            const resp = await api.get(`/admin/listino/import-result/${result.jobId}`, {
                responseType: 'blob',
            });
            const url  = window.URL.createObjectURL(new Blob([resp.data]));
            const link = document.createElement('a');
            link.href  = url;
            link.setAttribute('download', result.filename || 'listino_import.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError(`Errore download: ${err.message}`);
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            const resp = await api.get('/admin/listino/template', { responseType: 'blob' });
            const url  = window.URL.createObjectURL(new Blob([resp.data]));
            const link = document.createElement('a');
            link.href  = url;
            link.setAttribute('download', 'template_listino_import.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError(`Errore download template: ${err.message}`);
        }
    };

    const stats = progress || {};
    const totale     = stats.totale || 0;
    const processati = stats.processati || 0;
    const percent    = totale > 0 ? Math.round((processati / totale) * 100) : 0;

    return (
        <Container className="py-4">
            <h2>
                <FontAwesomeIcon icon={faFileExcel} className="me-2 text-success" />
                Importa Listino Fornitore
            </h2>
            <p className="text-muted mb-1">
                Carica un file Excel/ODS con la colonna <code>EAN</code> (e opzionalmente{' '}
                <code>quantita</code>, <code>prezzo_ibs</code>, <code>prezzo_negozio</code>,{' '}
                <code>costo_acquisto</code>). Lo script recupera automaticamente titolo, regista,
                attori, trama, ecc. da <strong>dvd-store.it</strong> e genera un file Excel pronto
                per l'import nel catalogo.
            </p>
            <p className="text-muted small mb-3">
                Non hai ancora il template?{' '}
                <Button variant="link" size="sm" className="p-0 align-baseline" onClick={handleDownloadTemplate}>
                    Scarica il template vuoto
                </Button>
            </p>

            <Card>
                <Card.Body>
                    <Form onSubmit={handleSubmit}>
                        <Form.Group as={Row} className="mb-3">
                            <Form.Label column sm={3}>File Excel / ODS</Form.Label>
                            <Col sm={9}>
                                <Form.Control
                                    type="file"
                                    accept=".xlsx,.xls,.ods"
                                    onChange={handleFileChange}
                                    disabled={uploading}
                                    required
                                />
                                {file && (
                                    <Form.Text className="text-success">
                                        ✅ {file.name}
                                    </Form.Text>
                                )}
                            </Col>
                        </Form.Group>

                        <Form.Group as={Row} className="mb-3">
                            <Form.Label column sm={3}>
                                Pausa tra richieste (s)
                            </Form.Label>
                            <Col sm={3}>
                                <Form.Control
                                    type="number"
                                    min={0.5}
                                    max={5}
                                    step={0.5}
                                    value={throttle}
                                    onChange={e => setThrottle(parseFloat(e.target.value))}
                                    disabled={uploading}
                                />
                                <Form.Text className="text-muted">
                                    1.5s = ~1 film ogni 2s. Aumenta se dvd-store ti blocca.
                                </Form.Text>
                            </Col>
                        </Form.Group>

                        <Form.Group as={Row}>
                            <Col sm={{ span: 9, offset: 3 }}>
                                <Button
                                    type="submit"
                                    variant="success"
                                    disabled={!file || uploading}
                                >
                                    {uploading ? (
                                        <>
                                            <Spinner as="span" animation="border" size="sm" className="me-2" />
                                            Scraping in corso…
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faCloudUploadAlt} className="me-2" />
                                            Avvia scraping
                                        </>
                                    )}
                                </Button>
                            </Col>
                        </Form.Group>
                    </Form>
                </Card.Body>
            </Card>

            {/* Progress */}
            {uploading && progress !== null && (
                <Card className="mt-3">
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <strong>
                                <FontAwesomeIcon icon={faSearch} className="me-2 text-primary" />
                                Ricerca su dvd-store.it
                            </strong>
                            <span className="text-muted small">
                                {totale > 0
                                    ? `${processati} / ${totale} EAN (${percent}%)`
                                    : 'Avvio…'}
                            </span>
                        </div>
                        <ProgressBar
                            now={totale > 0 ? percent : 100}
                            animated
                            striped
                            variant="success"
                            label={totale > 0 ? `${percent}%` : ''}
                        />
                        {ultimoEan && (
                            <div className="text-muted small mt-1">
                                Ultimo EAN: <code>{ultimoEan}</code>
                            </div>
                        )}
                        <Row className="mt-3 text-center">
                            <Col>
                                <div className="text-muted small">Trovati</div>
                                <div className="fs-5 text-success">{stats.trovati || 0}</div>
                            </Col>
                            <Col>
                                <div className="text-muted small">Non trovati</div>
                                <div className="fs-5 text-warning">{stats.non_trovati || 0}</div>
                            </Col>
                            <Col>
                                <div className="text-muted small">Errori</div>
                                <div className={`fs-5 ${(stats.errori || 0) > 0 ? 'text-danger' : ''}`}>
                                    {stats.errori || 0}
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            )}

            {error && <Alert variant="danger" className="mt-3">{error}</Alert>}

            {/* Risultato finale */}
            {result && (
                <Card className="mt-3 border-success">
                    <Card.Header className="bg-success text-white">
                        <FontAwesomeIcon icon={faFileExcel} className="me-2" />
                        Scraping completato
                    </Card.Header>
                    <Card.Body>
                        <Table size="sm" borderless className="mb-3">
                            <tbody>
                                <tr>
                                    <td className="text-muted" style={{ width: 180 }}>EAN totali</td>
                                    <td><strong>{result.stats?.totale ?? 0}</strong></td>
                                </tr>
                                <tr>
                                    <td className="text-muted">Trovati su dvd-store</td>
                                    <td>
                                        <Badge bg="success">{result.stats?.trovati ?? 0}</Badge>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="text-muted">Non trovati</td>
                                    <td>
                                        <Badge bg="warning" text="dark">
                                            {result.stats?.non_trovati ?? 0}
                                        </Badge>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="text-muted">Errori</td>
                                    <td>
                                        <Badge bg={result.stats?.errori > 0 ? 'danger' : 'secondary'}>
                                            {result.stats?.errori ?? 0}
                                        </Badge>
                                    </td>
                                </tr>
                            </tbody>
                        </Table>

                        <Button variant="success" onClick={handleDownload}>
                            <FontAwesomeIcon icon={faDownload} className="me-2" />
                            Scarica Excel per import
                        </Button>

                        <div className="mt-3 p-3 bg-light rounded small text-muted">
                            <strong>Passo successivo:</strong> vai su{' '}
                            <a href="/nuovo/film/import">Catalogo → Importa da CSV/Excel</a>,
                            carica il file appena scaricato e procedi in due passaggi:
                            prima con <em>Aggiorna esistenti = No</em> (crea i film),
                            poi con <em>Aggiorna esistenti = Sì</em> (applica le immagini).
                        </div>
                    </Card.Body>
                </Card>
            )}
        </Container>
    );
};

export default ListinoImport;
